import React, { useCallback, useMemo, useRef, useState } from "react";
import { endpoints } from "../utils/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import useApi from "../hooks/useApi";
import { useNotifications } from "../context/NotificationProvider";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import Modal from "react-modal";
import infos from "../assets/images/icon-info.svg";
import { syncWebToken } from "../utils/webPushSync";
import useAuth from "../hooks/useAuth";
import { createEventDedupeKey, pushDataLayerEvent } from "../utils/gtmDataLayer";
import { buildUserDataObject } from "../utils/gtmEcommerce";
import { formatBirthdayForGtm } from "../utils/gtmEventFields";
import useRegistrationForm from "../features/auth/useRegistrationForm";
import { mapRegistrationErrors } from "../features/auth/errorMapping";
import { normalizePhone, validatePhoneForOtp } from "../features/auth/otpValidation";
import AuthService from "../services/auth.service";
import "../styles/auth-remaster.css";

const customStyles2 = {
  content: {
    width: "33%",
    top: "50%",
    padding: "0px",
    left: "50%",
    right: "auto",
    bottom: "auto",
    borderRadius: "0px",
    boxShadow: "none",
    maxWidth: "450px",
    margin: "auto",
    textAlign: "center",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
  },
};

Modal.setAppElement("#root");

const getRequestErrorMessage = (error, t) => {
  if (error?.code === "ERR_NETWORK") {
    return t("register.global.network");
  }
  if (error?.response?.status === 429) {
    return (
      error?.response?.data?.message ||
      t("register.global.retryLater")
    );
  }
  if (error?.response?.status >= 500) {
    return t("register.global.server");
  }
  return t("register.global.unexpected");
};

const getFieldValue = (fallback, value) => fallback || value || "";

const InscriptionForm = () => {
  const { i18n, t } = useTranslation("auth");
  const { setAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const notify = useNotifications();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const fieldRefs = useRef({});

  const {
    provider,
    token,
    email,
    firstname,
    lastname,
    fcm_token,
    socialCompletion,
  } = location.state || {};

  const isSocialCompletion = useMemo(
    () => Boolean(socialCompletion && provider && token),
    [socialCompletion, provider, token]
  );

  const {
    values,
    fieldErrors,
    isSubmitting,
    setIsSubmitting,
    setFieldErrors,
    passwordChecks,
    handleBlur,
    setFieldValue,
    validateAll,
    normalizePayload,
  } = useRegistrationForm({
    initialValues: {
      firstname: firstname || "",
      lastname: lastname || "",
      email: email || "",
      password: "",
      confirmPassword: "",
      phone: "",
      civility: null,
      birthdate: "",
      acceptCGU: false,
      token: token || "",
      provider: provider || "",
      fcm_token: fcm_token || "",
      is_new: isSocialCompletion ? true : undefined,
    },
    mode: isSocialCompletion ? "social" : "classic",
    t,
  });

  const pushSignUpEvent = ({ createdUser, phoneNumber, emailAddress, firstName, lastName, birthday }) => {
    const userData = buildUserDataObject({
      user: createdUser,
      phoneNumber,
      emailAddress,
      firstName,
      lastName,
      userId: createdUser?.id,
      isConnectedUser: Boolean(createdUser?.id),
    });

    const signupUserData = {
      email_address: userData.email_address || undefined,
      first_name: userData.first_name || undefined,
      last_name: userData.last_name || undefined,
      phone_number: userData.phone_number || undefined,
      user_id: userData.user_id || undefined,
      birthday: formatBirthdayForGtm(birthday ?? createdUser?.birthdate),
    };

    pushDataLayerEvent(
      "sign_up",
      {
        new_customer: true,
        user_data: Object.fromEntries(
          Object.entries(signupUserData).filter(([, value]) => value !== undefined)
        ),
      },
      {
        dedupeKey: createEventDedupeKey(
          "sign_up",
          createdUser?.id || emailAddress || phoneNumber || "register"
        ),
      }
    );
  };

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === "civility") {
      setFieldValue("civility", values.civility === value ? null : value);
      handleBlur("civility");
      return;
    }

    if (type === "checkbox") {
      setFieldValue(name, checked);
      return;
    }

    setFieldValue(name, value);
  };

  const handlePhoneChange = (value) => {
    setFieldValue("phone", value);
  };

  const setFieldRef = useCallback((name, element) => {
    fieldRefs.current[name] = element;
  }, []);

  const focusFirstErrorField = useCallback((errors) => {
    const fieldOrder = [
      "firstname",
      "lastname",
      "phone",
      "email",
      ...(!isSocialCompletion ? ["password", "confirmPassword"] : []),
      "birthdate",
      "acceptCGU",
    ];

    const firstErrorField = fieldOrder.find((name) => errors[name]);
    if (!firstErrorField) return;

    const target = fieldRefs.current[firstErrorField];
    if (!target) return;

    const headerOffset = window.innerWidth <= 786 ? 88 : 108;
    const rect = target.getBoundingClientRect();
    const absoluteTop = window.scrollY + rect.top;

    window.scrollTo({
      top: Math.max(absoluteTop - headerOffset, 0),
      behavior: "smooth",
    });

    const focusable =
      target.matches?.("input, textarea, select")
        ? target
        : target.querySelector?.("input, textarea, select") ||
          target.querySelector?.("button");

    window.setTimeout(() => {
      focusable?.focus?.({ preventScroll: true });
    }, 180);
  }, [isSocialCompletion]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalizedPhone = normalizePhone(values.phone);
    const nextValues = { ...values, phone: normalizedPhone };
    const phoneError = validatePhoneForOtp(normalizedPhone, (key) => t(`register.${key}`));
    const localErrors = validateAll(nextValues);
    const nextErrors = {
      ...localErrors,
      ...(phoneError ? { phone: phoneError } : {}),
    };

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      focusFirstErrorField(nextErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = normalizePayload(nextValues);
      const response = isSocialCompletion
        ? {
            data: await AuthService.socialLogin(payload, api, provider, {
              suppressValidationToasts: true,
            }),
          }
        : await api.post(
            endpoints.register,
            payload,
            {
              headers: { "Content-Type": "application/json" },
              suppressValidationToasts: true,
            }
          );

      const data = response.data;

      if (data?.user?.status === "ACTIVE" || data?.user?.status === "PENDING_PHONE_VERIFICATION") {
        pushSignUpEvent({
          createdUser: data?.user,
          phoneNumber: normalizedPhone,
          emailAddress: data?.user?.email || values.email,
          firstName: data?.user?.firstname || values.firstname,
          lastName: data?.user?.lastname || values.lastname,
          birthday: values.birthdate || data?.user?.birthdate,
        });

        const accessToken = data?.token;
        const roles = data?.user?.roles;
        const status = data?.user?.status;
        const refresh = data?.refresh_token;
        const active = data?.user?.status === "ACTIVE" || data?.user?.status === "PENDING_PHONE_VERIFICATION";

        localStorage.setItem("accessToken", data?.token);
        setAuth({
          user: data?.user,
          roles,
          accessToken,
          refresh,
          active,
          status,
        });

        if (data?.user?.status === "PENDING_PHONE_VERIFICATION") {
          notify.success(t("register.activationSuccess"));
          navigate("/VerificationNumero", { replace: true });
        } else {
          if (data?.user?.id) {
            syncWebToken({
              userId: data.user.id,
              appName: "client",
              axios: api,
            }).catch(() => {});
          }
          navigate("/", { replace: true });
        }
      } else {
        notify.error(t("register.global.unexpected"));
      }
    } catch (error) {
      if (error?.response?.status === 422) {
        const { fieldErrors: mappedErrors, globalMessage } = mapRegistrationErrors(error);
        setFieldErrors((prev) => ({ ...prev, ...mappedErrors }));
        if (Object.keys(mappedErrors).length > 0) {
          focusFirstErrorField(mappedErrors);
        }
        if (globalMessage && Object.keys(mappedErrors).length === 0) {
          notify.error(globalMessage);
        }
      } else {
        notify.error(getRequestErrorMessage(error, t));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const urlsFly = {
    cgu: i18n.language === "fr"
      ? "https://flyspa.ch/conditions-generales-utilisation/"
      : "https://flyspa.ch/en/general-terms-of-use/",
    privacy: i18n.language === "fr"
      ? "https://flyspa.ch/politique-de-confidentialite/"
      : "https://flyspa.ch/en/privacy-policy/",
  };

  const passwordChecklist = [
    ["minLength", t("register.passwordChecks.minLength")],
    ["uppercase", t("register.passwordChecks.uppercase")],
    ["lowercase", t("register.passwordChecks.lowercase")],
    ["number", t("register.passwordChecks.number")],
    ["special", t("register.passwordChecks.special")],
  ];

  return (
    <>
      <div>
        <Header />
        <div className="form-inscription">
          <form onSubmit={handleSubmit} noValidate>
            <h2 className="auth-register-title">{t("register.title")}</h2>

            <div className="form-choix">
              <div className="form-radio">
                <input
                  type="checkbox"
                  name="civility"
                  value="MS"
                  id="MS"
                  checked={values.civility === "MS"}
                  onChange={handleChange}
                />
                <label htmlFor="MS">{t("register.civility.ms")}</label>
              </div>
              <div className="form-radio">
                <input
                  type="checkbox"
                  name="civility"
                  value="MR"
                  id="MR"
                  checked={values.civility === "MR"}
                  onChange={handleChange}
                />
                <label htmlFor="MR">{t("register.civility.mr")}</label>
              </div>
            </div>
            {fieldErrors.civility ? (
              <small className="auth-field-error">{fieldErrors.civility}</small>
            ) : null}

            <div className="form-text">
              <div ref={(element) => setFieldRef("firstname", element)}>
              <label>{t("register.firstName")} *</label>
              <input
                type="text"
                name="firstname"
                value={values.firstname}
                onChange={handleChange}
                onBlur={() => handleBlur("firstname")}
                required
              />
              {fieldErrors.firstname ? (
                <small className="auth-field-error">{fieldErrors.firstname}</small>
              ) : null}
              </div>
            </div>

            <div className="form-text">
              <div ref={(element) => setFieldRef("lastname", element)}>
              <label>{t("register.lastName")} *</label>
              <input
                type="text"
                name="lastname"
                value={values.lastname}
                onChange={handleChange}
                onBlur={() => handleBlur("lastname")}
                required
              />
              {fieldErrors.lastname ? (
                <small className="auth-field-error">{fieldErrors.lastname}</small>
              ) : null}
              </div>
            </div>

            <div className="form-text">
              <div ref={(element) => setFieldRef("phone", element)}>
              <label>{t("register.phoneNumber")} *</label>
              <PhoneInput
                defaultCountry="ch"
                type="tel"
                name="phone"
                value={values.phone}
                disableCountryGuess={true}
                forceDialCode={true}
                onChange={handlePhoneChange}
                onBlur={() => handleBlur("phone")}
                required
              />
              {fieldErrors.phone ? (
                <small className="auth-field-error">{fieldErrors.phone}</small>
              ) : null}
              </div>
            </div>

            <div className="form-text">
              <div ref={(element) => setFieldRef("email", element)}>
              <label>{t("register.email")} *</label>
              <input
                type="email"
                name="email"
                value={getFieldValue(email, values.email)}
                onChange={handleChange}
                onBlur={() => handleBlur("email")}
                readOnly={Boolean(email)}
                required
              />
              {fieldErrors.email ? (
                <small className="auth-field-error">{fieldErrors.email}</small>
              ) : null}
              </div>
            </div>

            {!isSocialCompletion && (
              <div className="form-text">
                <div ref={(element) => setFieldRef("password", element)}>
                <label>{t("register.password")} *</label>
                <div className="pass-eye">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={() => handleBlur("password")}
                    required
                  />
                  <button type="button" onClick={() => setShowPassword((prev) => !prev)}>
                    {showPassword ? t("register.hidePassword") : t("register.showPassword")}
                  </button>
                </div>
                <div className="auth-password-checklist">
                  {passwordChecklist.map(([key, label]) => (
                    <span
                      key={key}
                      className={`auth-password-check ${passwordChecks[key] ? "is-valid" : ""}`}
                    >
                      {label}
                    </span>
                  ))}
                </div>
                {fieldErrors.password ? (
                  <small className="auth-field-error">{fieldErrors.password}</small>
                ) : null}
                </div>
              </div>
            )}

            {!isSocialCompletion && (
              <div className="form-text">
                <div ref={(element) => setFieldRef("confirmPassword", element)}>
                <label>{t("register.confirmPassword")} *</label>
                <div className="pass-eye">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    onBlur={() => handleBlur("confirmPassword")}
                    required
                  />
                  <button type="button" onClick={() => setShowPassword((prev) => !prev)}>
                    {showPassword ? t("register.hidePassword") : t("register.showPassword")}
                  </button>
                </div>
                {fieldErrors.confirmPassword ? (
                  <small className="auth-field-error">{fieldErrors.confirmPassword}</small>
                ) : null}
                </div>
              </div>
            )}

            <div className="form-text">
              <div ref={(element) => setFieldRef("birthdate", element)}>
              <label>{t("register.birthdate")}</label>
              <div className="infos">
                <input
                  type="date"
                  name="birthdate"
                  value={values.birthdate}
                  onChange={handleChange}
                  onBlur={() => handleBlur("birthdate")}
                  onFocus={(event) => event.target.showPicker && event.target.showPicker()}
                  className="custom-date-input"
                />
                <button type="button" onClick={openModal}></button>
              </div>
              {fieldErrors.birthdate ? (
                <small className="auth-field-error">{fieldErrors.birthdate}</small>
              ) : null}
              </div>
            </div>

            <div className="form-check auth-consent-block" ref={(element) => setFieldRef("acceptCGU", element)}>
              <div className="auth-consent-row">
                <input
                  type="checkbox"
                  name="acceptCGU"
                  id="acceptCGU"
                  checked={values.acceptCGU}
                  onChange={handleChange}
                  onBlur={() => handleBlur("acceptCGU")}
                  required
                />
                <label htmlFor="acceptCGU" className="accepter-les-CGU-et">
                  <span>{t("register.accept1")} </span>
                  <a
                    href={urlsFly.cgu}
                    style={{ color: "#000", textDecoration: "underline" }}
                    className="text-wrapper-9"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("register.accept2")}
                  </a>
                  <span> {t("register.accept3")} </span>
                  <a
                    href={urlsFly.privacy}
                    style={{ color: "#000", textDecoration: "underline" }}
                    className="text-wrapper-9"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("register.accept4")}
                  </a>
                  <span> *</span>
                </label>
              </div>
              {fieldErrors.acceptCGU ? (
                <small className="auth-field-error auth-form-checkbox-error">
                  {fieldErrors.acceptCGU}
                </small>
              ) : null}
            </div>

            <button type="submit" className="btn-submit" disabled={isSubmitting}>
              {isSubmitting ? t("register.creatingAccount") : t("register.createAccount")}
            </button>
          </form>
        </div>
      </div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles2}
        contentLabel="Example Modal"
        id="custom-popup-content"
      >
        <div className="contentModal">
          <button className="close-btn" onClick={closeModal}>
            <img src={infos} alt="Fermer" />
          </button>
          <div className="modal">
            <p className="modal-text text-birth">{t("birth-text")}</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default InscriptionForm;
